import { createStore, createEvent, createEffect, sample } from 'effector';
import { createTelegramApiClient } from '../utils/apiClient';
import { AuthenticatedRequest, createAuthenticatedRequest } from './types';
import { userStore } from './userStore';
import { setGlobalSessionExpired } from './sessionStore';
import { activationStore, resetActivation, setMessage, setShowRewardCongratulations } from './activationStore';
import { MainButtonStore, setMainButton } from './mainButtonStore';
import { SecondaryButtonStore, setSecondaryButton } from './secondButtonStore';
import { $skipQrScan } from './skipQrScan';
import { closeTelegramMiniApp } from '../utils/telegramWebApp';

// Типы
export interface GetRewardRequest {
  rewardId: number;
}

export interface RewardResponse {
  success: boolean;
  message: string;
  rewardAmount?: number;
  transactionId?: string;
}

export interface RewardError {
  message: string;
  isSessionExpired?: boolean;
}

// События
export const getReward = createEvent();
export const setRewardMessage = createEvent<string>();
export const setRewardLoading = createEvent<boolean>();
export const setRewardError = createEvent<string | null>();
export const scanAgain = createEvent();

// Эффекты
export const getRewardFx = createEffect<AuthenticatedRequest<GetRewardRequest>, RewardResponse, RewardError>(async ({ data, initData }) => {
  console.log('activateQrCodeFx started with data:', data);
  
  try {
    const telegramClient = createTelegramApiClient(initData);
    const result = await telegramClient.post('/reward', data, {
      loadingText: 'Запрос на получение награды...'
    });
    
    console.log('Response result:', result);
    return result;
  } catch (error: any) {
    console.log('Error details:', error);
    
    // Проверяем, является ли ошибка связанной с истекшим сеансом
    const isSessionExpired = error.message?.toLowerCase().includes('сеанс истек') ||
                            error.message?.toLowerCase().includes('session expired') ||
                            error.message?.toLowerCase().includes('401');
    
    console.log('Error detected, isSessionExpired:', isSessionExpired);
    window.Telegram?.WebApp?.showAlert(error.message || 'Ошибка активации QR-кода');
    
    throw Object.assign(new Error(error.message || 'Ошибка активации QR-кода'), {
      isSessionExpired
    });
  }
});

// Store
export interface RewardState {
  message: string | null;
  isLoading: boolean;
  error: string | null;
  isSessionExpired: boolean;
}

export const rewardStore = createStore<RewardState>({
  message: null,
  isLoading: false,
  error: null,
  isSessionExpired: false,
})
  .on(setRewardMessage, (state, message) => ({ ...state, message }))
  .on(setRewardLoading, (state, isLoading) => ({ ...state, isLoading }))
  .on(setRewardError, (state, error) => ({ ...state, error }))
  .on(getRewardFx.pending, (state, isLoading) => ({ ...state, isLoading }))
  .on(getRewardFx.doneData, (state, result) => {
    // Показываем страницу поздравления вместо алерта    
    return { 
      ...state,
      success: result.success,
      message: "Твоя криптовалюта уже в пути к твоему кошельку.\n Мы сообщим, как только она будет зачислена.", 
    } as RewardState;
  })
  .on(getRewardFx.failData, (state, error) => {
    console.log('getRewardFx failed with error:', error);
    console.log('isSessionExpired:', error.isSessionExpired);
    return {
      ...state,
      error: error.message,
      isSessionExpired: error.isSessionExpired || false,
      isLoading: false
    };
  });

// Логика
sample({
  clock: getReward,
  source: { user: userStore, activation: activationStore },
  filter: ({ activation }) => !!activation.rewardId,
  fn: ({user, activation}) => {    
    const result = createAuthenticatedRequest({
      rewardId: activation.rewardId,
    } as GetRewardRequest, user.telegramInitData);
    console.log('Sample function result:', result);
    return result;
  },
  target: getRewardFx
});

sample({
  clock: setShowRewardCongratulations,
  filter: (flag) => flag,
  fn: () => ({
    text: 'Получить приз',
    action: () => getReward(),
    state: 'get_reward',
  } as MainButtonStore),
  target: setMainButton,
});
sample({
  clock: setShowRewardCongratulations,
  filter: (flag) => flag,
  fn: () => ({
    text: '',
    action: null,
    state: 'hidden',
  } as SecondaryButtonStore),
  target: setSecondaryButton,
});
sample({
  clock: getRewardFx.doneData,
  fn: () => {
    const annotation = 'Твоя криптовалюта уже в пути к твоему кошельку.\n Мы сообщим, как только она будет зачислена.';
    return annotation;
  },
  target: setMessage,
});
sample({
  clock: getRewardFx.doneData,
  source: $skipQrScan,
  filter: (skipQrScan) => !skipQrScan,
  fn: () => ({
    text: 'Хочу еще!',
    action: () => scanAgain(),
    state: 'waiting_reward',
  } as MainButtonStore),
  target: setMainButton,
});
sample({
  clock: getRewardFx.doneData,
  source: $skipQrScan,
  filter: (skipQrScan) => skipQrScan,
  fn: () => ({
    text: 'Закрыть',
    action: () => closeTelegramMiniApp(),
    state: 'hidden',
  } as MainButtonStore),
  target: setMainButton,
});
sample({
  clock: getRewardFx.doneData,
  fn: () => ({
    text: 'Отлично!',
    action: () => closeTelegramMiniApp(),
    state: 'waiting_reward',
  } as SecondaryButtonStore),
  target: setSecondaryButton,
});
// Обрабатываем SESSION_EXPIRED ошибки
sample({
  clock: getRewardFx.failData,
  filter: (error: RewardError) => error.isSessionExpired === true,
  fn: () => {
    console.log('Session expired in getRewardFx, setting session expired');
    return true;
  },
  target: setGlobalSessionExpired
});

sample({
  clock: scanAgain,  
  target: resetActivation
});
